import {ariaLabelDate} from '@github-ui/aria-label-date'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import safeStorage from '@github-ui/safe-storage'
import {useAnalytics} from '@github-ui/use-analytics'
import {ChevronDownIcon, ChevronRightIcon, StopIcon} from '@primer/octicons-react'
import {Button, Flash, IconButton, Label, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useEffect, useRef, useState} from 'react'
import {useResolveThreadMutation} from '@github-ui/pull-requests/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '@github-ui/pull-requests/use-unresolve-thread-mutation'

import {CommentErrorFallback} from './CommentErrorFallback'
import {
  AutomatedCommentSource,
  CommentReviewVariantType,
  type CommentAuthor,
  type Thread,
} from '../../conversation-types'
import styles from './FileReviewThread.module.css'
import {ReviewThread, type ReviewThreadProps} from './ReviewThread'
import {fileLevelCommentKey} from '../../types'
import {isLoggedIn} from '@github-ui/client-env'
import {InlineAutomatedReviewThreadActions} from './InlineAutomatedReviewThreadActions'

export interface FileReviewThreadProps extends Pick<ReviewThreadProps, 'subjectId' | 'viewerData'> {
  fileAnchor?: string
  filePath: string
  thread: Thread
  ghostUser?: CommentAuthor
  manuallyUpdateCommentsWithThisThreadId?: string
  isFirstThread?: boolean
}

export function FileReviewThread({
  fileAnchor,
  filePath,
  thread,
  isFirstThread,
  ghostUser,
  manuallyUpdateCommentsWithThisThreadId,
  ...rest
}: FileReviewThreadProps) {
  const reviewThreadRef = useRef<HTMLDivElement>(null)
  const safeLocalStorage = safeStorage('localStorage')
  const isResolved = thread?.isResolved
  const [errorMessage, setErrorMessage] = useState('')
  const [isCollapsed, setIsCollapsed] = useState<boolean>(isResolved ?? false)

  useEffect(() => {
    const storedState = localStorage.getItem(`reviewThreadIsCollapsed_${thread.id}`)
    if (storedState !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsCollapsed(JSON.parse(storedState))
    } else if (isResolved) {
      setIsCollapsed(true)
    }
  }, [isResolved, thread.id])

  const handleToggleCollapsed = () => {
    window.requestAnimationFrame(() => {
      safeLocalStorage.setItem(`reviewThreadIsCollapsed_${thread.id}`, JSON.stringify(!isCollapsed))
    })
    setIsCollapsed((prevIsCollapsed: boolean) => !prevIsCollapsed)
  }

  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(filePath, fileLevelCommentKey)
  const handleResolveThread = () => {
    if (!thread || thread.isResolved) return

    resolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          setIsCollapsed(true)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )

    sendAnalyticsEvent('comments.resolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(filePath, fileLevelCommentKey)
  const handleUnresolveThread = () => {
    if (!thread) return

    unresolveThreadMutation(
      {threadId: thread.id},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          setIsCollapsed(false)
          safeLocalStorage.removeItem(`reviewThreadIsCollapsed_${thread.id}`)
        },
      },
    )

    sendAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  if (!thread) return null

  const hasComments = !!thread.commentsData?.comments && thread.commentsData.comments.length > 0

  if (!hasComments) return null

  const isAutomatedReviewThread =
    thread?.commentsData?.comments[0]?.reviewVariantType === CommentReviewVariantType.Automated
  const automatedComment = isAutomatedReviewThread ? thread.commentsData?.comments[0]?.automatedComment : null
  const dismissableAutomatedComment = automatedComment && automatedComment.dismissable
  const licenseComplianceAutomatedComment =
    automatedComment && automatedComment.source === AutomatedCommentSource.LicenseCompliance

  const isThreadResolved = !!thread.isResolved
  const userCanResolve = isLoggedIn()
  const isResolveable =
    userCanResolve && thread.commentsData?.comments?.some(comment => comment.state?.toUpperCase() !== 'PENDING')

  const createdAt = thread.commentsData?.comments[0]?.createdAt
  const commentAuthorLogin = thread.commentsData?.comments[0]?.author?.login ?? 'Unknown Author'

  let resolveButtonLabel = `${isResolved ? 'Unresolve' : 'Resolve'} comment`
  if (createdAt) {
    const createdAtFormatted = ariaLabelDate(new Date(createdAt))
    resolveButtonLabel = `${
      isResolved ? 'Unresolve' : 'Resolve'
    } ${commentAuthorLogin}'s comment, ${createdAtFormatted}`
  }

  return (
    <ErrorBoundary fallback={<CommentErrorFallback />}>
      <div className="rounded-2 bgColor-default" ref={reviewThreadRef}>
        <div className={clsx('px-1', styles.ReviewThreadContainer)}>
          <IconButton
            aria-label={isCollapsed ? 'Expand comment' : 'Collapse comment'}
            icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
            size="small"
            variant="invisible"
            onClick={handleToggleCollapsed}
            data-is-first-collapse-button={isFirstThread}
          />
          <h2 className={styles.ReviewThreadHeading}>Comment on file</h2>

          <div className={styles.ReviewThreadInnerContainer}>
            {thread.positioning?.isOutdated && <Label variant="secondary">Outdated</Label>}

            {isThreadResolved && <Label variant="secondary">Resolved</Label>}
          </div>
        </div>
        <Suspense
          fallback={
            <div className={styles.SpinnerWrapper}>
              <Spinner />
              <p>Loading comments</p>
            </div>
          }
        >
          {errorMessage && (
            <Flash variant="danger" className="m-2">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
          {!isCollapsed && (
            <>
              <ReviewThread
                lineMarkersKey={fileLevelCommentKey}
                batchingEnabled
                filePath={filePath}
                gridCellId={`${filePath}-file-level-markers`}
                isInlineComment={false}
                thread={thread}
                shouldLimitHeight={false}
                {...rest}
                ghostUser={ghostUser}
              />
              {isResolveable && !dismissableAutomatedComment && !licenseComplianceAutomatedComment && (
                <div className="d-flex flex-items-center p-2 border-top flex-wrap">
                  <Button
                    aria-label={resolveButtonLabel}
                    onClick={isThreadResolved ? handleUnresolveThread : handleResolveThread}
                    className="mr-2"
                  >
                    {isResolved ? 'Unresolve comment' : 'Resolve comment'}
                  </Button>
                  {isThreadResolved && thread.resolvedBy && (
                    <span className="fgColor-muted f6">
                      <b>{thread.resolvedBy}</b> marked this comment as resolved
                    </span>
                  )}
                </div>
              )}
              {dismissableAutomatedComment && (
                <InlineAutomatedReviewThreadActions
                  automatedComment={automatedComment}
                  thread={thread}
                  filePath={filePath}
                  lineMarkersKey={fileLevelCommentKey}
                  setErrorMessage={setErrorMessage}
                  setIsCollapsed={setIsCollapsed}
                  safeLocalStorage={safeLocalStorage}
                />
              )}
            </>
          )}
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}

try{ FileReviewThread.displayName ||= 'FileReviewThread' } catch {}